import React, {
  useState,
  useRef,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import { Thumbnail } from 'react-pdf';
import Outline from './Outline';
import styles from './Pdf.module.css';
import classNames from 'classnames';
import PdfDocument from './PdfDocument';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List, areEqual } from 'react-window';

const thumbnailWidth = 98;
const thumbnailTopDownMargin = 10;

const renderThumbnail = memo(({ data, index, style }) => {
  const { pdf, scrollToPage } = data;
  return (
    <div style={style}>
      <Thumbnail
        pdf={pdf}
        pageNumber={index + 1}
        width={thumbnailWidth}
        onItemClick={scrollToPage}
      />
    </div>
  );
}, areEqual);

renderThumbnail.propTypes = {
  data: PropTypes.shape({
    pdf: PropTypes.object.isRequired,
    scrollToPage: PropTypes.func.isRequired
  }).isRequired,
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired
};

const Pdf = (props) => {
  const { pdfFileURL, source } = props;
  const [pdf, setPdf] = useState(null);
  const [outline, setOutline] = useState([]);
  const [outlineHasDeepNesting, setOutlineHasDeepNesting] = useState(false);
  const [isThumbnailShown, setIsThumbnailShown] = useState(true);
  const [pageViews, setPageViews] = useState(new Map());
  const documentRef = useRef(null);
  const getPageSize = (pageIndex) => {
    // a page view is an array with 4 elements: [minWidth, minHeight, maxWidth, maxHeight]
    const view = pageViews.get(pageIndex);
    const pageHeight = view[3] - view[1];
    const pageWidth = view[2] - view[0];
    const thumbnailHeight = (thumbnailWidth * pageHeight) / pageWidth;

    return thumbnailHeight + thumbnailTopDownMargin;
  }

  const onDocumentLoad = async (pdf, pageViews) => {
    const outline = (await pdf.getOutline()) ?? [];
    setOutlineHasDeepNesting(
      outline.some((i) => i.items && i.items.length > 0)
    );
    
    setOutline(outline);
    setIsThumbnailShown(outline.length === 0);
    setPdf(pdf);
    setPageViews(pageViews);
  };

  const scrollToPage = ({ pageIndex }) => {
    documentRef.current?.scrollToPage(pageIndex);
  };

  return (
    <div
      id={styles.outerContainer}
      className={classNames(styles.sidebarOpen)}
    >
      <div id={styles.sidebarContainer}>
        <div id={styles.toolbarSidebar}>
          <div id={styles.toolbarSidebarLeft}>
            <div
              id={styles.sidebarViewButtons}
              className={`${styles.splitToolbarButton}`}
            >
              <button
                id={styles.viewThumbnail}
                className={classNames(styles.toolbarButton, {
                  [styles.toggled]: isThumbnailShown,
                })}
                title="Show Thumbnails"
                aria-controls="thumbnailView"
                onClick={() => setIsThumbnailShown(true)}
              >
                <span>Thumbnails</span>
              </button>
              <button
                id={styles.viewOutline}
                className={classNames(styles.toolbarButton, {
                  [styles.toggled]: !isThumbnailShown,
                })}
                title="Show Document Outline (double-click to expand/collapse all items)"
                aria-controls="outlineView"
                disabled={outline.length === 0}
                onClick={() => setIsThumbnailShown(false)}
              >
                <span>Document Outline</span>
              </button>
            </div>
          </div>
        </div>
        <div id={styles.sidebarContent}>
          {pdf && pageViews?.size && (
            <div style={{ height: '100%', width: '100%' }}>
              <AutoSizer
                className={classNames({ [styles.hidden]: !isThumbnailShown })}
              >
                {({ height, width }) => (
                  <List
                  // we are subtracting 1 from height and width to avoid exceeding 
                  // actual height and width which will trigger flickering
                    height={height - 1}
                    width={width - 1}
                    itemSize={getPageSize}
                    itemCount={pdf.numPages}
                    itemData={{ pdf, scrollToPage }}
                  >
                    {renderThumbnail}
                  </List>
                )}
              </AutoSizer>
            </div>
          )}
          <div
            id={styles.outlineView}
            className={classNames({
              [styles.hidden]: isThumbnailShown,
              [styles.treeWithDeepNesting]: outlineHasDeepNesting,
            })}
          >
            {pdf && (
              <Outline pdf={pdf} outline={outline} onItemClick={scrollToPage} />
            )}
          </div>
        </div>
      </div>

      <div id={styles.mainContainer}>
        <div id={styles.viewerContainer}>
          <PdfDocument
            pdfFileURL={pdfFileURL}
            source={source}
            onDocumentLoad={onDocumentLoad}
            scrollToPage={scrollToPage}
            ref={documentRef}
          />
        </div>
      </div>
    </div>
  );
};

Pdf.propTypes = {
  pdfFileURL: PropTypes.string,
  source: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.instanceOf(ArrayBuffer),
    PropTypes.instanceOf(Uint8Array)
  ])
};

export default Pdf;
